<template>
	<div class="graph-card"
		:class="[type, `type-${graphType}`]"
		:style="{
			'--aboveColor': aboveColor,
			'--notReachedColor': notReachedColor,
			'--reachedColor': reachedColor,
			'--noGoalColor': noGoalColor
		}"  
	>
		<div class="graph-header">
			<h3>{{ title }}</h3>
		</div>
		<div class="graph-main">
			<template v-if="graphType === 'progress'">
				<div class="graph-body">
					<div class="progress-container">
						<div class="indicators-container" v-if="this.goal !== null">
							<div class="indicator goal"
								:style="{ width: percentageGoal+'%' }"
							>
								<i class="fas fa-caret-down"></i>
							</div>
						</div>
						<div class="progress-bar">
							<div 
								class="progress"
								:class="{ 
									'above': percentageValue > percentageGoal,
									'reached': percentageValue === percentageGoal,
									'reverse': reverse,
									'noGoal': goal === null
								}"
								:style="{ width: percentageValue+'%' }">
							</div>
						</div>
					</div>
					<h2 class="statistic-value"
						:class="{
							'above': percentageValue > percentageGoal,
							'reached': percentageValue === percentageGoal,
							'reverse': reverse,
							'noGoal': goal === null
						}"
					>{{ value }}</h2>
				</div>
				<div class="graph-footer">
					<div class="graph-legend">
						<div class="graph-legend-container" v-if="this.goal !== null" @click="ChangeGoalQuestion(type)">
							<i class="fas fa-check-circle"></i>
							<p>{{ goal }}</p>
						</div>
						<div class="graph-legend-container" v-else @click="ChangeGoalQuestion(type)">
							<i class="fas fa-minus-circle" style="color: var(--reachedColor)"></i>
							<p style="color: var(--cardfontColor)">Meta não definida</p>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="graph-body">
					<div class="circular-donut"
						:class="{
							'above': percentageValue > percentageGoal,
							'reached': percentageValue === percentageGoal,
							'reverse': reverse,
							'noGoal': goal === null
						}"
						:style="{
							'--progress': animatedProgress,
							'--fontSize': donutFontSize+'rem',
						}"
					>
						<div class="indicators-container" v-if="this.goal !== null">
							<div class="indicator goal">
								<i class="fas fa-caret-down"></i>
							</div>
						</div>
						<div class="statistic-value-container">
							<h2 class="statistic-value"
								:class="{
									'above': percentageValue > percentageGoal,
									'reached': percentageValue === percentageGoal,
									'reverse': reverse,
									'noGoal': goal === null
								}"
							>{{ value }}<span v-if="type === 'no_show'">%</span></h2>
						</div>
					</div>
					
				</div>
				<div class="graph-footer">
					<div class="graph-legend">
						<div class="graph-legend-container" v-if="this.goal !== null" @click="ChangeGoalQuestion(type)">
							<i class="fas fa-check-circle"></i>
							<p>{{ goal }}</p>
						</div>
						<div class="graph-legend-container" v-else @click="ChangeGoalQuestion(type)">
							<i class="fas fa-minus-circle" style="color: var(--reachedColor)"></i>
							<p style="color: var(--cardfontColor)">Meta não definida</p>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>  
</template>

<script>
import Swal from 'sweetalert2';

export default {
	name: 'GraphCard',
	props: {
		title: {
			type: String,
			required: true
		},
		goal: {
			type: [Number, null],
			required: false,
			default: null
		},
		value: {
			type: Number,
			required: true
		},
		type: {
			type: String,
			required: true,
		},
		hasFilters: {
			type: Boolean,
			default: false
		},
		reverse: {
			type: Boolean,
			default: false
		},
		graphType: {
			type: String,
			default: 'progress'
		}
	},
	data() {
		return {
			animatedProgress: 0,
			reachedColor: '#F5C747',
			notReachedColor: '#fe0f38',
			aboveColor: '#105DD9',
			noGoalColor: '#F5C747',
			progressInterval: null
		}
	},
	computed: {
		max() {
			if (this.goal === null) return 100;
			if (this.goal === 0) return 100;
			return this.goal + (this.goal / 3);
		},
		percentageGoal() {
			if (this.goal === null) return 0;
			if (this.goal === 0) return 0;
			return (100 / this.max) * this.goal;
		},
		percentageValue() {
			if (this.goal === null) return Math.min(100, (this.value / 100) * 100);
			if (this.goal === 0) return Math.min(100, this.value * 100);
			return (100 / this.max) * this.value;
		},
		donutFontSize() {
			if(this.type === 'no_show') {
				const INITIAL_SIZE = 4
				const MINIMUN_SIZE = 2.5
				const MEDIUM_SIZE = 3.2
				
				const chars = `${this.value}`.split('').length + 1

				if(chars > 2 && chars < 5) return MEDIUM_SIZE
				if(chars >= 5) return MINIMUN_SIZE

				return INITIAL_SIZE
			}

			const INITIAL_SIZE = 4
			const MINIMUN_CHARS = 2
			const MINIMUN_SIZE = 2
			
			const chars = `${this.value}`.split('').length
			if(chars <= MINIMUN_CHARS) return INITIAL_SIZE
			
			const diff = INITIAL_SIZE - chars
			if(diff <= 0) return MINIMUN_SIZE

			const size = (INITIAL_SIZE - diff) + (diff / 100)
			if(size < MINIMUN_SIZE) return MINIMUN_SIZE
			
			return size
		}
	},
	watch: {
		percentageValue: {
			handler(newVal, oldVal) {
				if (this.progressInterval) {
					clearInterval(this.progressInterval);
					this.progressInterval = null;
				}

				if (typeof oldVal !== 'number' || Math.abs(newVal - oldVal) < 1) {
					this.animatedProgress = newVal;
					return;
				}

				if (this.animatedProgress > 100) this.animatedProgress = 100;
				if (this.animatedProgress < 0) this.animatedProgress = 0;

				const step = newVal > oldVal ? 1 : -1;

				this.progressInterval = setInterval(() => {
					if (this.animatedProgress === newVal) {
						clearInterval(this.progressInterval);
						this.progressInterval = null;
						return;
					}
					this.animatedProgress += step;

					if ((step > 0 && this.animatedProgress > newVal) ||
						(step < 0 && this.animatedProgress < newVal)) {
						this.animatedProgress = newVal;
						clearInterval(this.progressInterval);
						this.progressInterval = null;
					}
				}, 5);
			},
			immediate: true
		},
		hasFilters: {
			handler(val) {
				if(!val) {
					const BLACK_COLOR = '#000'

					this.aboveColor = BLACK_COLOR
					this.reachedColor = BLACK_COLOR
					this.notReachedColor = BLACK_COLOR
					this.noGoalColor = BLACK_COLOR
				} else {
					this.aboveColor = '#105DD9'
					this.reachedColor = '#F5C747'
					this.notReachedColor = '#fe0f38'
					this.noGoalColor = '#F5C747'
				}
			},
			immediate: true
		},
	},
	methods: {
		ChangeGoalQuestion(key) {
			Swal.fire({
				title: 'Preencha com a nova meta',
				input: 'text',
				inputValue: this.goal !== null && this.goal !== undefined ? String(this.goal) : '',
				showCancelButton: true,
				reverseButtons: true,
				inputValidator: (value) => {
					if(value !== '' && isNaN(value)) {
						return 'Precisa ser um número válido!';
					}
				},
				cancelButtonText: 'Cancelar'
			})
				.then((response) => {
					if(response.isConfirmed) {
						this.$emit('change-goal', {
							key,
							value: response.value === '' ? null : +response.value
						})
					}
				})
		}
	}
}
</script>

<style lang="scss" scoped>
	.graph-card {
		--reachedColor: #F5C747;
		--notReachedColor: #fe0f38;
		--aboveColor: #105DD9;
		--noGoalColor: #F5C747;

		padding: 1rem;
		background-color: var(--cardBackgroundColor);
		border: 1px solid var(--cardBackgroundColor);
		border-radius: 1rem;
		display: flex;
		flex-direction: column;
		align-items: center;

		.graph-header {
			width: 100%;
			display: flex;

			h3 {
				color: var(--cardfontColor);
				font-size: 2.3rem;
				text-transform: uppercase;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
				min-height: 2.6rem;
				line-height: 1;
				margin-bottom: 0;
			}
		}

		.graph-main {
			width: 100%;
			flex-grow: 1;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;

			.graph-body {
				width: 100%;
				flex-grow: 1;
				display: flex;
				align-items: center;
				gap: 3rem;

				.progress-container {
					width: 100%;
					position: relative;
					margin-top: 2rem;

					.indicators-container {
						width: 100%;
						height: 3rem;
						position: absolute;
						top: -2.4rem;
						overflow: hidden;

						.indicator {
							display: flex;
							justify-content: flex-end;
							font-size: 3rem;
							position: relative;

							i {
								position: absolute;
								right: calc(2rem / 2 * -1);
							}
						}

						.goal i {
							color: var(--reachedColor);
						}
					}

					.progress-bar {
						width: 100%;
						height: 2rem;
						background-color: var(--graphBackgroundColor);
						border-radius: 10px;
						overflow: hidden;

						.progress {
							height: 100%;
							transition: width 0.3s ease;
							
							background-color: var(--notReachedColor);
							&.reached {
								background-color: var(--reachedColor);
							}

							&.above {
								background-color: var(--aboveColor);
							}

							&.noGoal {
								background-color: var(--noGoalColor);
							}

							&.reverse {
								background-color: var(--aboveColor);
								
								&.reached {
									background-color: var(--reachedColor);
								}

								&.above {
									background-color: var(--notReachedColor);
								}

								&.noGoal {
									background-color: var(--noGoalColor);
								}
							}
						}
					}
				}

				.statistic-value {
					font-size: 6rem;
					margin-right: 2rem;

					&.bigger {
						font-size: 9rem;
					}

					color: var(--notReachedColor);
					&.reached {
						color: var(--reachedColor);
					}

					&.above {
						color: var(--aboveColor);
					}

					&.noGoal {
						color: var(--noGoalColor);
					}

					&.reverse {
						color: var(--aboveColor);
						
						&.reached {
							color: var(--reachedColor);
						}

						&.above {
							color: var(--notReachedColor);
						}

						&.noGoal {
							color: var(--noGoalColor);
						}
					}
				}
			}

			.graph-footer {
				min-width: 30%;
				display: flex;
				justify-content: center;

				.graph-legend {
					.graph-legend-container {
						display: flex;
						justify-content: center;
						align-items: baseline;
						gap: .8rem;
						cursor: pointer;

						i {
							color: var(--reachedColor);
							font-size: 1.5rem;
						}

						p {
							font-size: 1.5rem;
							margin-bottom: 0;
							color: var(--cardfontColor);
						}
					}
				}
			}
		}
			
		&.type-circular {
			.graph-header {
				h3 {
					font-size: 1.8rem;
				}
			}

			.graph-main {
				flex-direction: row;

				.graph-body {
					justify-content: center;

					.circular-donut {
						--progress: 0;
						--color: var(--notReachedColor);
						--size: 10rem;
						--fontSize: 4rem;

						&.reached {
							--color: var(--reachedColor);
						}

						&.above {
							--color: var(--aboveColor);
						}

						&.noGoal {
							--color: var(--noGoalColor);
						}

						&.reverse {
							--color: var(--aboveColor);

							&.reached {
								--color: var(--reachedColor);
							}

							&.above {
								--color: var(--notReachedColor);
							}

							&.noGoal {
								--color: var(--noGoalColor);
							}
						}

						width: var(--size);
						height: var(--size);
						border-radius: 50%;
						background: conic-gradient(var(--color) calc(var(--progress) * 1%), var(--graphBackgroundColor) 0);
						display: flex;
						align-items: center;
						justify-content: center;
						transition: background 0.3s ease;
						position: relative;

						.indicators-container {
							--indicatorSize: 3rem;

							width: 100%;
							height: 100%;
							position: absolute;

							.indicator {
								position: relative;
								font-size: var(--indicatorSize);
							}

							.goal {
								transform: rotate(270deg);
								// left: calc(((var(--size) / 2) + (var(--size) / 100) * 10) * -1);
								// top: calc((var(--size) / 2) + (var(--size) / 100) * 25);
								left: -5.8rem;
								top: -22px;

								i {
									color: var(--reachedColor);
								}
							}
						}

						.statistic-value-container {
							width: calc(var(--size) - 2rem);
							height: calc(var(--size) - 2rem);
							border: 1px solid var(--cardBackgroundColor);
							border-radius: 50%;
							background-color: var(--cardBackgroundColor);
							display: flex;
							align-items: center;
							justify-content: center;
							z-index: 1;
						}

						.statistic-value {
							font-size: var(--fontSize);
							margin: 0;
							text-align: center;
							width: fit-content;
						}
					}
				}
			}
		}
	}
</style>